import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { RemoveError } from '@zerops/fe/ngrx';
import {
  map,
  switchMap,
  catchError,
  filter
} from 'rxjs/operators';
import { SnackService } from '@app/common/snack';
import { ErrorTranslationService } from '@app/services';
import { TicketsBaseApi } from './tickets-base.api';
import {
  ActionTypes,
  UnlockRequest,
  UnlockLocalSuccess,
  UnlockFail,
  ApprovePriceOfferRequest,
  ApprovePriceOfferLocalSuccess,
  ApprovePriceOfferFail,
  RejectPriceOfferRequest,
  RejectPriceOfferLocalSuccess,
  RejectPriceOfferFail,
  ConfirmRealizationDateRequest,
  ConfirmRealizationDateLocalSuccess,
  ConfirmRealizationDateFail,
  RejectRealizationDateRequest,
  RejectRealizationDateLocalSuccess,
  RejectRealizationDateFail
} from './tickets-base.action';
import { TranslateService } from '@ngx-translate/core';

// separated effect file containing effects listening to actions fired even if user not authorized (ticket public pages)
@Injectable()
export class TicketsBaseNotAuthorizedEffect {

  // unlock
  onUnlockRequest$ = createEffect(() => this._actions$.pipe(
    ofType<UnlockRequest>(ActionTypes.UnlockRequest),
    switchMap((({ payload }) => this._api
      .unlock$(payload)
      .pipe(
        map((response) => new UnlockLocalSuccess(response, payload)),
        catchError((err) => this._errorTranslation
          .get$(err)
          .pipe(map((data) => new UnlockFail(data, payload)))
        )
      )
    ))
  ));

  // price offer
  onApprovePriceOfferRequest$ = createEffect(() => this._actions$.pipe(
    ofType<ApprovePriceOfferRequest>(ActionTypes.ApprovePriceOfferRequest),
    switchMap((({ payload, meta: { lastAdminTicketMessageId, clientId, skipSuccessNotification } }) => this._api
      .approvePriceOffer$(payload, lastAdminTicketMessageId, clientId)
      .pipe(
        map((response) => new ApprovePriceOfferLocalSuccess(response, payload, !!skipSuccessNotification)),
        catchError((err) => this._errorTranslation
          .get$(err)
          .pipe(map((data) => new ApprovePriceOfferFail(data, payload)))
        )
      )
    ))
  ));

  onApprovePriceOfferLocalSuccess$ = createEffect(() => this._actions$.pipe(
    ofType<ApprovePriceOfferLocalSuccess>(ActionTypes.ApprovePriceOfferLocalSuccess),
    filter(({ skipSuccessNotification }) => !skipSuccessNotification),
    switchMap(() => this._translate
      .get([
        'common.close',
        'ticketBase.approvePriceOfferSuccess'
      ])
      .pipe(
        switchMap((translations) => this._snack.success$(
          translations['ticketBase.approvePriceOfferSuccess'],
          translations['common.close']
        ))
      )
    )
  ), { dispatch: false });

  onApprovePriceOfferFail$ = createEffect(() => this._actions$.pipe(
    ofType<ApprovePriceOfferFail>(ActionTypes.ApprovePriceOfferFail),
    switchMap((action) => this._snack.translatedFail$(
      action.meta?.message ?? '',
      'common.close'
    )),
    map(() => new RemoveError(ActionTypes.ApprovePriceOfferFail))
  ));

  onRejectPriceOfferRequest$ = createEffect(() => this._actions$.pipe(
    ofType<RejectPriceOfferRequest>(ActionTypes.RejectPriceOfferRequest),
    switchMap((({ payload, meta: { clientId, messageText, skipSuccessNotification } }) => this._api
      .rejectPriceOffer$(payload, clientId, messageText)
      .pipe(
        map((response) => new RejectPriceOfferLocalSuccess(response, payload, !!skipSuccessNotification)),
        catchError((err) => this._errorTranslation
          .get$(err)
          .pipe(map((data) => new RejectPriceOfferFail(data, payload)))
        )
      )
    ))
  ));

  onRejectPriceOfferLocalSuccess$ = createEffect(() => this._actions$.pipe(
    ofType<RejectPriceOfferLocalSuccess>(ActionTypes.RejectPriceOfferLocalSuccess),
    filter(({ skipSuccessNotification }) => !skipSuccessNotification),
    switchMap(() => this._translate
      .get([
        'common.close',
        'ticketBase.rejectPriceOfferSuccess'
      ])
      .pipe(
        switchMap((translations) => this._snack.success$(
          translations['ticketBase.rejectPriceOfferSuccess'],
          translations['common.close']
        ))
      )
    )
  ), { dispatch: false });

  onRejectPriceOfferFail$ = createEffect(() => this._actions$.pipe(
    ofType<RejectPriceOfferFail>(ActionTypes.RejectPriceOfferFail),
    switchMap((action) => this._snack.translatedFail$(
      action.meta?.message ?? '',
      'common.close'
    )),
    map(() => new RemoveError(ActionTypes.RejectPriceOfferFail))
  ));

  // realization date
  onConfirmRealizationDateRequest$ = createEffect(() => this._actions$.pipe(
    ofType<ConfirmRealizationDateRequest>(ActionTypes.ConfirmRealizationDateRequest),
    switchMap((({ ticketId, meta: { clientId, skipSuccessNotification } }) => this._api
      .confirmRealizationDate$(ticketId, clientId)
      .pipe(
        map(() => new ConfirmRealizationDateLocalSuccess(ticketId, !!skipSuccessNotification)),
        catchError((err) => this._errorTranslation
          .get$(err)
          .pipe(map((data) => new ConfirmRealizationDateFail(data, ticketId)))
        )
      )
    ))
  ));

  onConfirmRealizationDateLocalSuccess$ = createEffect(() => this._actions$.pipe(
    ofType<ConfirmRealizationDateLocalSuccess>(ActionTypes.ConfirmRealizationDateLocalSuccess),
    filter(({ skipSuccessNotification }) => !skipSuccessNotification),
    switchMap(() => this._translate
      .get([
        'common.close',
        'ticketBase.confirmRealizationDateSuccess'
      ])
      .pipe(
        switchMap((translations) => this._snack.success$(
          translations['ticketBase.confirmRealizationDateSuccess'],
          translations['common.close']
        ))
      )
    )
  ), { dispatch: false });

  onConfirmRealizationDateFail$ = createEffect(() => this._actions$.pipe(
    ofType<ConfirmRealizationDateFail>(ActionTypes.ConfirmRealizationDateFail),
    switchMap((action) => this._snack.translatedFail$(
      action.meta?.message ?? '',
      'common.close'
    )),
    map(() => new RemoveError(ActionTypes.ConfirmRealizationDateFail))
  ));

  onRejectRealizationDateRequest$ = createEffect(() => this._actions$.pipe(
    ofType<RejectRealizationDateRequest>(ActionTypes.RejectRealizationDateRequest),
    switchMap((({ ticketId, meta: { clientId, messageText, skipSuccessNotification } }) => this._api
      .rejectRealizationDate$(ticketId, clientId, messageText)
      .pipe(
        map(() => new RejectRealizationDateLocalSuccess(ticketId, !!skipSuccessNotification)),
        catchError((err) => this._errorTranslation
          .get$(err)
          .pipe(map((data) => new RejectRealizationDateFail(data, ticketId)))
        )
      )
    ))
  ));

  onRejectRealizationDateLocalSuccess$ = createEffect(() => this._actions$.pipe(
    ofType<RejectRealizationDateLocalSuccess>(ActionTypes.RejectRealizationDateLocalSuccess),
    filter(({ skipSuccessNotification }) => !skipSuccessNotification),
    switchMap(() => this._translate
      .get([
        'common.close',
        'ticketBase.rejectRealizationDateSuccess'
      ])
      .pipe(
        switchMap((translations) => this._snack.success$(
          translations['ticketBase.rejectRealizationDateSuccess'],
          translations['common.close']
        ))
      )
    )
  ), { dispatch: false });

  onRejectRealizationDateFail$ = createEffect(() => this._actions$.pipe(
    ofType<RejectRealizationDateFail>(ActionTypes.RejectRealizationDateFail),
    switchMap((action) => this._snack.translatedFail$(
      action.meta?.message ?? '',
      'common.close'
    )),
    map(() => new RemoveError(ActionTypes.RejectRealizationDateFail))
  ));

  constructor(
    private _actions$: Actions,
    private _api: TicketsBaseApi,
    private _translate: TranslateService,
    private _errorTranslation: ErrorTranslationService,
    private _snack: SnackService
  ) { }
}
